import React, { useEffect, useState } from "react";
import { Plus, Sparkles, Truck, PackageCheck, PackageOpen, X, UserCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ORDER_STATUS, OrderStatusPill, ugx, when } from "./shared";

const selectCls = "mt-1 h-10 w-full border border-slate-300 rounded-sm px-3 text-sm bg-white";
const FILTERS = [["open", "Open"], ["REQUESTED", "Requested"], ["RELEASED", "Released"], ["PICKED_UP", "With rider"], ["DELIVERED", "Empty due"], ["CLOSED", "Closed"], ["CANCELLED", "Cancelled"], ["", "All"]];

const NewOrderDialog = ({ open, onOpenChange, stations, preset, onSaved }) => {
  const [search, setSearch] = useState("");
  const [households, setHouseholds] = useState([]);
  const [hid, setHid] = useState("");
  const [sid, setSid] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setHid(preset?.household_id || ""); setSid(preset?.station_id || ""); setNotes(""); setSearch("");
  }, [open, preset]);
  useEffect(() => {
    if (!open) return;
    api.get("/households", { params: { page_size: 50, search: search || undefined } }).then(({ data }) => setHouseholds(data.data || [])).catch(() => {});
  }, [open, search]);
  const hh = households.find((h) => h.id === hid);
  useEffect(() => { if (hh && !sid && hh.station_id) setSid(hh.station_id); }, [hh]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!hid) { toast.error("Choose a household"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/refill-orders", { household_id: hid, station_id: sid || null, notes, source: preset ? "SUGGESTED" : "MANUAL" });
      toast.success(`Order ${data.number} created`);
      onOpenChange(false); onSaved();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Could not create order"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-md" data-testid="new-order-dialog">
        <DialogHeader><DialogTitle className="font-heading">New refill order</DialogTitle>
          <DialogDescription>The station releases a full cylinder; a rider delivers it and brings the empty back.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {!preset && <div><Label>Find household</Label><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, phone…" className="rounded-sm mt-1" data-testid="new-order-search" /></div>}
          <div><Label>Household</Label>
            <select value={hid} onChange={(e) => { setHid(e.target.value); setSid(""); }} className={selectCls} data-testid="new-order-household">
              <option value="">Select household</option>
              {preset && !households.some((h) => h.id === preset.household_id) && <option value={preset.household_id}>{preset.household_name}</option>}
              {households.map((h) => <option key={h.id} value={h.id}>{h.household_name}{h.phone ? ` · +${h.phone}` : ""}</option>)}
            </select></div>
          <div><Label>Station</Label>
            <select value={sid} onChange={(e) => setSid(e.target.value)} className={selectCls} data-testid="new-order-station">
              <option value="">Household's station / nearest</option>
              {stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select></div>
          <div><Label>Notes</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm mt-1" data-testid="new-order-notes" /></div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-sm bg-brand-600 hover:bg-brand-700" data-testid="new-order-submit">{saving ? "Creating…" : "Create order"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Suggestions = ({ onCreate, refreshKey }) => {
  const [rows, setRows] = useState([]);
  const [threshold, setThreshold] = useState(null);
  useEffect(() => { api.get("/refill-orders/suggestions").then(({ data }) => { setRows(data.data || []); setThreshold(data.threshold_pct); }).catch(() => {}); }, [refreshKey]);
  if (!rows.length) return null;
  return (
    <div className="border border-flame-200 bg-flame-50/60 rounded-sm p-4" data-testid="order-suggestions">
      <div className="flex items-center gap-2 text-xs font-semibold text-flame-700"><Sparkles size={14} /> {rows.length} household{rows.length === 1 ? "" : "s"} below the {threshold}% low-level alert</div>
      <div className="mt-3 flex flex-wrap gap-2">
        {rows.map((r, i) => (
          <div key={r.household_id} className="bg-white border border-slate-200 rounded-sm px-3 py-2 flex items-center gap-3 text-xs" data-testid={`suggestion-${i}`}>
            <div><div className="font-medium text-slate-900">{r.household_name}</div>
              <div className="text-slate-500">{r.cylinder_type} · <span className="text-red-600 font-semibold">{r.remaining_pct}%</span> · {r.station_name || "no station"}</div></div>
            <Button size="sm" className="h-7 rounded-sm bg-flame-600 hover:bg-flame-700" onClick={() => onCreate(r)} data-testid={`suggestion-create-${i}`}>Create order</Button>
          </div>
        ))}
      </div>
    </div>
  );
};

const Act = ({ icon: Icon, label, onClick, testId, tone = "outline" }) => (
  <Button size="sm" variant={tone} className="h-7 rounded-sm text-[11px] px-2" onClick={onClick} data-testid={testId}><Icon size={12} className="mr-1" />{label}</Button>
);

const RiderDialog = ({ order, onOpenChange, onSaved }) => {
  const [riders, setRiders] = useState([]);
  const [rid, setRid] = useState("");
  useEffect(() => { if (order) api.get("/users").then(({ data }) => setRiders((data.data || []).filter((u) => u.role === "field_officer"))).catch(() => {}); }, [order]);
  const save = async () => {
    try { await api.post(`/refill-orders/${order.id}/assign`, { rider_id: rid }); toast.success("Rider assigned"); onOpenChange(false); onSaved(); }
    catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!order} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-sm" data-testid="assign-rider-dialog">
        <DialogHeader><DialogTitle className="font-heading">Assign rider · {order?.number}</DialogTitle><DialogDescription>Riders are field officers.</DialogDescription></DialogHeader>
        <select value={rid} onChange={(e) => setRid(e.target.value)} className={selectCls} data-testid="assign-rider-select">
          <option value="">Select rider</option>
          {riders.map((r) => <option key={r.id} value={r.id}>{r.name} · +{r.phone}</option>)}
        </select>
        <DialogFooter><Button disabled={!rid} onClick={save} className="rounded-sm bg-brand-600 hover:bg-brand-700" data-testid="assign-rider-save">Assign</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const OrdersPanel = ({ stations, onChanged }) => {
  const [filter, setFilter] = useState("open");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState(null);
  const [riderFor, setRiderFor] = useState(null);
  const [key, setKey] = useState(0);
  const load = () => {
    const status = filter === "open" ? "REQUESTED,RELEASED,PICKED_UP,DELIVERED" : filter;
    api.get("/refill-orders", { params: { status: status || undefined } }).then(({ data }) => setRows(data.data || [])).catch(() => {});
  };
  useEffect(load, [filter, key]); // eslint-disable-line
  const refresh = () => { setKey((k) => k + 1); onChanged?.(); };
  const [busy, setBusy] = useState(null);
  const act = async (o, action, body) => {
    if (busy) return;
    setBusy(o.id);
    try { await api.post(`/refill-orders/${o.id}/${action}`, body); toast.success(`${o.number} updated`); refresh(); }
    catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Action failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="orders-panel">
      <Suggestions refreshKey={key} onCreate={(r) => { setPreset(r); setOpen(true); }} />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map(([v, l]) => (
            <button key={l} onClick={() => setFilter(v)} data-testid={`orders-filter-${l.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${filter === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        <Button size="sm" className="rounded-sm bg-brand-600 hover:bg-brand-700" onClick={() => { setPreset(null); setOpen(true); }} data-testid="orders-new-btn"><Plus size={14} className="mr-1" /> New order</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="orders-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
            <tr>{["Order", "Household", "Cylinder", "Station", "Rider", "Status", "Updated", ""].map((h) => <th key={h} className="text-left px-4 py-2">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-400">No orders.</td></tr>}
            {rows.map((o, i) => (
              <tr key={o.id} className="border-t border-slate-100 align-middle" data-testid={`order-row-${i}`}>
                <td className="px-4 py-2 font-mono text-xs text-slate-800">{o.number}{o.source === "SUGGESTED" && <Sparkles size={11} className="inline ml-1 text-flame-500" />}</td>
                <td className="px-4 py-2"><div className="text-slate-900">{o.household_name}</div><div className="text-[11px] text-slate-500">{o.household_phone ? `+${o.household_phone}` : ""}</div></td>
                <td className="px-4 py-2 text-xs text-slate-700">{o.cylinder_type}<div className="text-[10px] text-slate-400">{ugx(o.refill_cost)}</div></td>
                <td className="px-4 py-2 text-xs text-slate-700">{o.station_name}</td>
                <td className="px-4 py-2 text-xs text-slate-700">{o.rider_name || "—"}</td>
                <td className="px-4 py-2"><OrderStatusPill status={o.status} testId={`order-status-${i}`} /></td>
                <td className="px-4 py-2 text-[11px] text-slate-500 whitespace-nowrap">{when(o.updated_at || o.created_at)}</td>
                <td className="px-4 py-2"><div className={`flex gap-1 justify-end flex-wrap ${busy === o.id ? "opacity-50 pointer-events-none" : ""}`}>
                  {o.status === "REQUESTED" && <Act icon={PackageOpen} label="Release" tone="default" onClick={() => act(o, "release", {})} testId={`order-release-${i}`} />}
                  {["REQUESTED", "RELEASED"].includes(o.status) && <Act icon={UserCheck} label="Rider" onClick={() => setRiderFor(o)} testId={`order-assign-${i}`} />}
                  {o.status === "RELEASED" && <Act icon={Truck} label="Picked up" onClick={() => act(o, "pickup")} testId={`order-pickup-${i}`} />}
                  {o.status === "PICKED_UP" && <Act icon={PackageCheck} label="Delivered" onClick={() => act(o, "deliver")} testId={`order-deliver-${i}`} />}
                  {["PICKED_UP", "DELIVERED"].includes(o.status) && <Act icon={PackageCheck} label="Empty returned" tone="default" onClick={() => act(o, "return-empty")} testId={`order-return-${i}`} />}
                  {["REQUESTED", "RELEASED"].includes(o.status) && <Act icon={X} label="Cancel" onClick={() => window.confirm(`Cancel ${o.number}?`) && act(o, "cancel", {})} testId={`order-cancel-${i}`} />}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-slate-400">Flow: {Object.values(ORDER_STATUS).slice(0, 5).map((s) => s.label).join(" → ")}. Delivery also records the cylinder swap on the household's meter.</div>
      <NewOrderDialog open={open} onOpenChange={setOpen} stations={stations} preset={preset} onSaved={refresh} />
      <RiderDialog order={riderFor} onOpenChange={() => setRiderFor(null)} onSaved={refresh} />
    </div>
  );
};
